<template>
  <div class="aspect-video w-full overflow-hidden rounded-lg bg-obsidian-700">
    <video
      v-if="isPlaying && canPlay"
      :key="media.fileUrl!"
      ref="playerRef"
      class="h-full w-full"
      autoplay
      controls
      playsinline
      :poster="media.posterFileUrl ?? undefined"
    />
    <button
      v-else
      type="button"
      class="relative flex h-full w-full items-center justify-center"
      :class="{ 'cursor-default': !canPlay }"
      :disabled="!canPlay"
      @click="handlePlay"
    >
      <img
        v-if="media.encodingStatus === 'READY' && media.posterFileUrl"
        :src="media.posterFileUrl"
        class="h-full w-full object-cover"
        alt=""
      />
      <div v-else class="flex h-full w-full items-center justify-center text-white">
        <VideoIcon class="h-10 w-10" />
      </div>
      <div
        v-if="canPlay"
        class="absolute inset-0 flex items-center justify-center bg-black/30 transition hover:bg-black/40"
      >
        <PlayIcon class="size-9 text-white" />
      </div>
    </button>
  </div>
</template>

<script lang="ts" setup>
import type { Media as RosMedia } from '@carvia/ros-client-types';
import { PlayIcon, VideoIcon } from 'lucide-vue-next';
import type Hls from 'hls.js';
import { computed, onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps<{
  media: RosMedia;
}>();

const playerRef = ref<HTMLVideoElement | null>(null);
const isPlaying = ref(false);
let hlsInstance: Hls | null = null;

const canPlay = computed(() => props.media.encodingStatus === 'READY' && !!props.media.fileUrl);

const destroyPlayer = () => {
  hlsInstance?.destroy();
  hlsInstance = null;
};

const setupPlayer = async () => {
  destroyPlayer();
  if (!canPlay.value || !isPlaying.value || !playerRef.value || !props.media.fileUrl) {
    return;
  }

  const video = playerRef.value;
  const fileUrl = props.media.fileUrl;

  const startPlayback = () => {
    void video.play();
  };

  const Hls = (await import('hls.js')).default;
  if (Hls.isSupported()) {
    hlsInstance = new Hls({ capLevelToPlayerSize: true });
    hlsInstance.loadSource(fileUrl);
    hlsInstance.attachMedia(video);
    hlsInstance.on(Hls.Events.MANIFEST_PARSED, startPlayback);
  } else {
    video.addEventListener('loadedmetadata', startPlayback, { once: true });
    video.src = fileUrl;
  }
};

const handlePlay = () => {
  if (!canPlay.value) return;
  isPlaying.value = true;
};

watch(
  () => props.media.id,
  () => {
    isPlaying.value = false;
    destroyPlayer();
  },
);

watch(
  () =>
    [isPlaying.value, props.media.fileUrl, props.media.encodingStatus, playerRef.value] as const,
  () => {
    void setupPlayer();
  },
  { flush: 'post' },
);

onBeforeUnmount(destroyPlayer);
</script>
